import Team from '@/components/Team/Team'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'

import './TennisBar.css'

import type { JsonMatchData, TennisScore, TennisEventState, TennisCompletedSet } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface TennisBarProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
}

const MULTIPLIERS = {
  setFontSize: 0.95,
  pointsFontSize: 1.05,
  serverDotSize: 0.22,
}

function resolvePoints(
  side: 'home' | 'away',
  score: TennisScore | undefined,
  eventState: TennisEventState | undefined
): string {
  const cg = eventState?.currentGame
  if (cg?.isTiebreak || cg?.isSuperTiebreak) {
    const tb = cg.tiebreakPoints
    if (!tb) return '0'
    return String((side === 'home' ? tb.home : tb.away) ?? 0)
  }
  return String(score?.currentGamePoints ?? '0')
}

function formatSetsCondensed(sets: TennisCompletedSet[]): string {
  return sets.map((s) => (s.tiebreak !== null ? `${s.games}(${s.tiebreak})` : String(s.games))).join(' ')
}

const TennisBar: FC<TennisBarProps> = ({ jsonData, isVisible = true }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)
  const homeScore = homeCompetitor?.score as TennisScore | undefined
  const awayScore = awayCompetitor?.score as TennisScore | undefined
  const eventState = jsonData?.eventState as TennisEventState | undefined

  const phase = eventState?.phase ?? 'PRE'
  const server = eventState?.server ?? 'home'
  const showLive = phase !== 'PRE'

  // Build the columns to render. Two payload shapes exist in the wild:
  //   1. Pre-allocated: sets[] sized to match format (BO3 → 3 zero entries);
  //      sets[currentSetIndex] is the live set.
  //   2. Completed-only: sets[] holds finished sets; currentSetGames is live.
  // Either way, show completed sets followed by the live set as one column.
  // Future, unplayed sets are dropped.
  const visibleSets = (sets: TennisCompletedSet[], currentSetGames: number): TennisCompletedSet[] => {
    const idx = eventState?.currentSetIndex
    if (typeof idx !== 'number') return sets
    if (!showLive) return sets.slice(0, idx)
    return sets[idx] ? sets.slice(0, idx + 1) : [...sets.slice(0, idx), { games: currentSetGames, tiebreak: null }]
  }

  const primaryBg = hexToRgba(theme.colors.primary, 0.99)
  const trapP = `calc(${theme.baseHeight} * 0.6)`
  const brandingClipPath = `polygon(calc(35% + ${trapP}) 0, calc(65% - ${trapP}) 0, 65% 100%, 35% 100%)`

  const cardStyle = {
    width: resolution.height,
    '--bar-row-height': `calc(${theme.baseHeight} * 1.667)`,
    '--primary-bg': primaryBg,
  } as React.CSSProperties

  const homeSummary = formatSetsCondensed(visibleSets(homeScore?.sets ?? [], homeScore?.currentSetGames ?? 0))
  const awaySummary = formatSetsCondensed(visibleSets(awayScore?.sets ?? [], awayScore?.currentSetGames ?? 0))
  const homePoints = showLive ? resolvePoints('home', homeScore, eventState) : ''
  const awayPoints = showLive ? resolvePoints('away', awayScore, eventState) : ''

  return (
    <div ref={animationRef} className="tennis-bar-content-wrapper">
      <div className="tennis-bar-card tennis-bar-grid" style={cardStyle} data-scorebar-panel>
        <div className="tennis-bar-branding-row" style={{ clipPath: brandingClipPath, height: theme.baseHeight }}>
          <div />
          <div className="tennis-bar-branding-center">
            <img
              src="/assets/frogbox-logo-mono.png"
              alt="FrogBox"
              style={{ height: `calc(${theme.baseHeight} * 0.5)` }}
            />
          </div>
          <div />
        </div>

        <div className="tennis-bar-row">
          <div className="tennis-bar-side">
            <Team data={homeCompetitor} isHome style={{ paddingRight: 0 }} />
            {server === 'home' && showLive && (
              <span
                className="tennis-bar-server-dot"
                aria-label="Serving"
                style={{
                  width: `calc(${theme.baseHeight} * ${MULTIPLIERS.serverDotSize})`,
                  height: `calc(${theme.baseHeight} * ${MULTIPLIERS.serverDotSize})`,
                  marginLeft: `calc(${theme.baseHeight} * 0.3)`,
                }}
              />
            )}
            <div
              className="tennis-bar-score-stack"
              style={{
                marginLeft: 'auto',
                marginRight: `calc(${theme.baseHeight} * 1)`,
                gap: `calc(${theme.baseHeight} * 0.4)`,
              }}
            >
              <span
                className="tennis-bar-sets"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.setFontSize})` }}
              >
                {homeSummary}
              </span>
              {showLive && (
                <span
                  className="tennis-bar-points"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.pointsFontSize})` }}
                >
                  {homePoints}
                </span>
              )}
            </div>
          </div>

          <div className="tennis-bar-center-group" />

          <div className="tennis-bar-side">
            <div
              className="tennis-bar-score-stack"
              style={{
                marginLeft: `calc(${theme.baseHeight} * 1)`,
                gap: `calc(${theme.baseHeight} * 0.4)`,
              }}
            >
              {showLive && (
                <span
                  className="tennis-bar-points"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.pointsFontSize})` }}
                >
                  {awayPoints}
                </span>
              )}
              <span
                className="tennis-bar-sets"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.setFontSize})` }}
              >
                {awaySummary}
              </span>
            </div>
            {server === 'away' && showLive && (
              <span
                className="tennis-bar-server-dot"
                aria-label="Serving"
                style={{
                  width: `calc(${theme.baseHeight} * ${MULTIPLIERS.serverDotSize})`,
                  height: `calc(${theme.baseHeight} * ${MULTIPLIERS.serverDotSize})`,
                  marginRight: `calc(${theme.baseHeight} * 0.3)`,
                }}
              />
            )}
            <Team data={awayCompetitor} style={{ paddingLeft: 0, marginLeft: 'auto' }} align="right" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TennisBar
